/**
 * 零碳园区平台 — 全局类型定义
 *
 * 静态 JSON 字段约定见 `public/data/nationalParks.json`、`public/data/provincialParks.json`
 * （中文列名与下方运行时映射字段对应）。
 *
 * 待开发类园区在 JSON 中通过 `园区等级: "待开发"` 与 `批准机关`（国务院 / 省政府）标识；
 * 源文件为 `public/data/pending-state-council.json`、`public/data/pending-provincial.json`，
 * 可由 `scripts/merge-park-data.cjs` 合并写入 `provincialParks.json`。
 */

// ---------------------------------------------------------------------------
// 省级园区 — 绿电资源规模（与数据源文案一致）
// ---------------------------------------------------------------------------

export type GreenElectricityScale =
  | '600MW以下'
  | '600MW~1000MW'
  | '1000MW~1500MW'
  | '1500MW~2000MW'
  | '2000MW以上';

// ---------------------------------------------------------------------------
// 附件（详情面板预览；路径对应 public/attachments 下文件）
// ---------------------------------------------------------------------------

export interface Attachment {
  /** 详情页展示的文件名（物理文件名第一个「_」之后部分） */
  label: string;
  /** 应用内路径，如 /attachments/provincialParks/园区名_文件名.docx（含 PUBLIC_URL 前缀） */
  path: string;
  type: 'image' | 'pdf' | 'doc';
}

/** 由 scripts/generate-attachments-index.cjs 根据 public/attachments 扫描生成 */
export interface AttachmentIndexEntry {
  subdir: string;
  parkName: string;
  storageName: string;
  label: string;
}

export interface AttachmentsIndex {
  version: number;
  generatedAt: string;
  entries: AttachmentIndexEntry[];
}

// ---------------------------------------------------------------------------
// 园区实体
// ---------------------------------------------------------------------------

/** 国家级园区 */
export interface NationalPark {
  /** 判别字段：国家级 */
  parkLevel: 'national';
  /** 唯一标识（由解析器生成） */
  id: string;
  /** 园区名称 */
  name: string;
  /** 省份 */
  province: string;
  /** 市 */
  city: string;
  /** 区（数据源可能为字符串或字符串数组，运行时已归一为展示用文案） */
  district: string;
  /** 文件 / 附件说明（如图纸、说明文件名） */
  file: string;
  /** 范围 */
  range: string;
  /** 建设周期 */
  buildingCycle: string;
  /** 对接情况 */
  dockingStatus: string;
  /** 可建设落地项目类型（多值；JSON/表格中多为英文逗号或分号分隔） */
  projectTypes: string[];
  /** 技术优势（多值；旧版 CSV 列，可为空） */
  technicalAdvantages: string[];
  /** 投资额档位：100亿以下 / 100亿-200亿 / 200亿-400亿 / 400亿以上 */
  investmentBracket: string;
  /** 基本情况（长文本） */
  basicSituation: string;
  /** 经度 */
  longitude: number;
  /** 纬度 */
  latitude: number;
}

/** 省级园区 */
export interface ProvincialPark {
  /** 判别字段：省级 */
  parkLevel: 'provincial';
  /** 唯一标识（由解析器生成） */
  id: string;
  /** 园区名称 */
  name: string;
  /** 省份 */
  province: string;
  /** 市 */
  city: string;
  /** 区 */
  district: string;
  /** 文件 / 附件说明 */
  file: string;
  /** 对接情况 */
  dockingStatus: string;
  /** 是否有绿电资源 */
  hasGreenElectricity: boolean;
  /** 绿电资源规模（MW 档位，规范取值为 GreenElectricityScale；兼容历史 CSV 任意原文） */
  greenElectricityScale: string;
  /** 主导产业类型（多值，CSV 中为分号分隔） */
  leadingIndustries: string[];
  /** 综合能源消费量（表格原文，如 20万~100万吨） */
  comprehensiveEnergyConsumption: string;
  /** 园区基本情况（长文本；零碳名单可无此项） */
  basicSituation: string;
  /** 申报失败原因 */
  failReason: string;
  /** 备注 */
  remark: string;
  /** 经度 */
  longitude: number;
  /** 纬度 */
  latitude: number;
}

/** 待开发园区（开发区名录：国务院或省政府批准） */
export interface PendingPark {
  parkLevel: 'pending';
  /** 批准层级：国务院对应国家级开发区名录，省政府对应省级开发区名录 */
  pendingApproval: 'state_council' | 'provincial_government';
  id: string;
  name: string;
  province: string;
  city: string;
  district: string;
  file: string;
  dockingStatus: string;
  hasGreenElectricity: boolean;
  greenElectricityScale: string;
  leadingIndustries: string[];
  comprehensiveEnergyConsumption: string;
  /** 园区地区生产总值，单位：亿元；无可核验精确值时为 null */
  grossRegionalProduct: number | null;
  /** 官方来源所对应的统计年份 */
  grossRegionalProductYear: number | null;
  /** 官方页面中的原始表述（保留“预计”“突破”等限定语） */
  grossRegionalProductStatement: string;
  /** 数据统计口径，如园区自身、区政合一、辐射带动区 */
  grossRegionalProductScope: string;
  /** 官方来源标题 */
  grossRegionalProductSource: string;
  /** 官方来源链接 */
  grossRegionalProductSourceUrl: string;
  /** 核验结果 */
  grossRegionalProductVerificationStatus:
    | 'verified'
    | 'official_estimate'
    | 'official_qualitative'
    | 'not_found';
  basicSituation: string;
  failReason: string;
  remark: string;
  longitude: number;
  latitude: number;
}

/** 国家级、省级与待开发园区联合类型 */
export type Park = NationalPark | ProvincialPark | PendingPark;

/** 与省级共用筛选字段的条目（省级零碳 + 待开发） */
export type ProvincialLikePark = ProvincialPark | PendingPark;

// ---------------------------------------------------------------------------
// 筛选、统计、地图状态
// ---------------------------------------------------------------------------

/** 园区层级筛选 */
export type ParkLevelFilter = 'all' | 'national' | 'provincial' | 'pending';

/** 左侧园区列表排序方式 */
export type ParksListSort =
  | 'administrative'
  | 'energy-asc'
  | 'energy-desc'
  | 'green-electricity-asc'
  | 'green-electricity-desc'
  | 'gdp-asc'
  | 'gdp-desc';

/**
 * 多维度筛选条件
 * - 未设置的字段表示不参与筛选
 */
export interface FilterCondition {
  /** 仅国家级 / 仅省级 / 全部 */
  parkLevel?: ParkLevelFilter;
  /** 省份（多选） */
  provinces?: string[];
  /** 市（多选） */
  cities?: string[];
  /** 区（多选） */
  districts?: string[];
  /** 对接情况（多选，原始文案精确匹配） */
  dockingStatuses?: string[];
  /** 国家级对接：已对接 / 未对接（归一化匹配） */
  nationalDocking?: '已对接' | '未对接';
  /** 国家级：可建设落地项目类型标签（多选 AND，每项须在 projectTypes 中命中） */
  nationalProjectTypeLabels?: string[];
  /** 省级 / 待开发：主导产业标签（多选 AND） */
  provincialLeadingIndustryLabels?: string[];
  /** 国家级：projectTypes 中需包含该子串或与某一项完全相等（数据层 AND 筛选） */
  nationalProjectTypeContains?: string;
  /** 国家级：技术优势关键词（子串匹配任一即可，可选） */
  technicalAdvantageKeywords?: string[];
  /** 省级 / 待开发：综合能源消费量档位（单选，与 comprehensiveEnergyConsumption 全等） */
  comprehensiveEnergyConsumptionTier?: string;
  /** 省级 / 待开发：绿电资源规模（多选，与 greenElectricityScale 全等） */
  greenElectricityScales?: string[];
  /** 待开发：园区地区生产总值档位 */
  pendingGrossRegionalProductTier?: string;
  /** 省级 / 待开发：leadingIndustries 中需包含该子串或与某一项完全相等（数据层 AND 筛选） */
  leadingIndustryContains?: string;
  /** 名称 / 备注等模糊搜索关键字 */
  keyword?: string;
}

/**
 * 数据层 / Hook 使用的筛选状态（与 UI 表单字段一一对应，AND 关系）
 */
export interface ParksDataFilter {
  level: ParkLevelFilter;
  /** 左侧列表排序；能源与绿电排序仅用于省级 / 待开发园区 */
  sortBy: ParksListSort;
  /** 国家级：可建设落地项目类型（多选）；每项均须在园区 projectTypes 中命中（AND） */
  nationalProjectTypes?: string[];
  /** 国家级：对接情况 */
  nationalDocking?: '已对接' | '未对接';
  /** 省级 / 待开发：综合能源消费量（单选） */
  comprehensiveEnergyConsumption?: string;
  /** 省级 / 待开发：绿电资源规模（多选）；与 greenElectricityScale 全等，OR 关系 */
  greenElectricityScales?: string[];
  /** 待开发：园区地区生产总值档位（单选） */
  pendingGrossRegionalProduct?: string;
  /** 省级 / 待开发：主导产业类型（多选）；每项均须命中（AND） */
  provincialLeadingIndustries?: string[];
}

/** Hook 默认筛选：展示全部园区 */
export const DEFAULT_PARKS_DATA_FILTER: ParksDataFilter = {
  level: 'all',
  sortBy: 'administrative',
};

/** 平台汇总统计 */
export interface Statistics {
  /** 国家级园区数量 */
  nationalCount: number;
  /** 省级园区数量 */
  provincialCount: number;
  /** 总数 */
  totalCount: number;
  /** 按省份统计 */
  byProvince: Record<
    string,
    {
      national: number;
      provincial: number;
    }
  >;
  /** 按对接情况统计 */
  byDockingStatus: Record<string, number>;
}

/** 地图视图与交互状态（供 ECharts GL / 业务层使用） */
export interface MapState {
  /** 当前视角中心经度 */
  centerLongitude: number;
  /** 当前视角中心纬度 */
  centerLatitude: number;
  /** 缩放级别或相机距离（与具体图表配置对齐） */
  zoom: number;
  /** 俯仰角（3D） */
  pitch?: number;
  /** 方位角 / 旋转（3D） */
  bearing?: number;
  /** 当前选中的园区 id */
  selectedParkId?: string;
  /** 高亮展示的园区 id 列表 */
  highlightedParkIds?: string[];
  /** 视图模式 */
  viewMode?: '2d' | '3d';
}

// ---------------------------------------------------------------------------
// 政策实体与筛选
// ---------------------------------------------------------------------------

export type QueryMode = 'parks' | 'policies' | 'docking' | 'profiles';

export type PolicyLevel = 'national' | 'provincial' | 'city' | 'park';

export interface Policy {
  id: string;
  title: string;
  level: PolicyLevel;
  policyTier: string;
  province: string;
  city: string;
  /** 园区级政策：对应国家级/省级园区名称（`city` 为空） */
  parkName?: string;
  issuingOffice: string;
  issuedAt: string;
  targetScopes: string[];
  industryTags: string[];
  supportTypes: string[];
  summary: string;
  coreGoal: string;
  supportPolicy: string;
  applicationSchedule: string;
  relatedFiles: string[];
  keywords: string[];
}

export type PolicyLevelFilter = 'all' | PolicyLevel;

export type PolicySortBy = 'levelThenIssuedAt' | 'relevance' | 'issuedAtDesc' | 'issuedAtAsc';

export interface PolicyFilter {
  keyword: string;
  level: PolicyLevelFilter;
  province?: string;
  city?: string;
  issuingOffice?: string;
  startDate?: string;
  endDate?: string;
  sortBy: PolicySortBy;
}

export const DEFAULT_POLICY_FILTER: PolicyFilter = {
  keyword: '',
  level: 'all',
  sortBy: 'levelThenIssuedAt',
};
